import { observer } from "mobx-react";
import { EnterpriseAccountModel, VerifyEmailController } from "./dialogDomain";
import { useDispatch, useTranslation, useUserPrefs } from "../stores/RootStoreContext";
import { isFailure, isOk, isPending } from "../common/common";
import { Box, VBox } from "../components-v2/Box";
import { Button, IconButton, LinkButton, ToggleButton } from "../components-v2/Button";
import { Markdown } from "../text/Markdown";
import { MTextInput } from "../components-v2/TextInput";
import { Alert } from "../components-v2/Alert";
import {
  copyfromAccessibleShareholdersDropdown,
  EditSingleShareholderController,
  editSingleShareholderTabs,
} from "../shareholderRegister/shareholders/editSingleShareholderDomain";
import { Dialog } from "../components-v2/Dialog";
import { TMessage } from "../i18n/tt";
import { FC, useEffect, useState } from "react";
import { DropdownMenu } from "../components-v2/Dropdown";
import { EditSingleShareholderForm } from "../shareholderRegister/shareholders/EditSingleShareholderForm";
import { LoadingIndicator } from "../components-v2/LoadingIndicator";
import { Tag } from "../components-v2/Tag";
import { adminSettings, adminUsersSettings } from "../utils/navigation";
import { Checkbox } from "../components-v2/Checkbox";
import { AlertState, ConfirmState } from "../common/alertConfirm";
import { ActorModel, ActorsSelectorModel, ActorType, actorTypeLabel } from "../types/SelectActors";
import { compact, last, noop } from "lodash";
import { Tabs } from "../components-v2/Tabs";
import { DataTable, TableColumn } from "../components-v2/DataTable";
import { formatOrgNum } from "../utils/other";
import { toDate } from "../utils/Dates";
import { RootStoreData } from "../events/rootStoreData";
import { AcceptCompanyTermsDialogView } from "../termOfService/TermsOfServiceDialog";
import {
  ActivateSubscriptionDialog,
  ActiveShareholderRegisterSubscriptionDialog,
  SubscriptionPaymentDialogNew,
} from "../payment/SubscriptionPayment";
import { SendInvitationDialog } from "../views/Users/SendInvitationDialog";
import {
  EmailEditForm,
  InviteNewAdminDialog,
  RevokeAllInAdminForm,
} from "../portfolio/contacts/contactsComponents";
import { NewShareClassDialog } from "../shareholderRegister/transactions/commonTransactionComponents";
import { LiveGeneralMeetingAnnouncementDialog } from "../meetings/generalMeeting/LiveGeneralMeetingAnnouncementDialog";
import {
  NorwegianCompanySearchDialog,
  SwedishCompanySearchDialog,
} from "../companysearch/SearchDialogs";
import NewMeetingDialogInitializer from "../meetings/NewMeetingDialog";
import { EditProjectNameDialog } from "../dbot/project/EditProjectNameDialog";
import { FilePreviewDialog } from "../components-v2/FilePreview";
import { NewPortfolioDialog } from "../portfolio/portfolioComponents";
import {
  findShareholderPricingInterval,
  getShareIntervals,
} from "../meetings/generalMeeting/electronicGeneralMeetingDomain";
import { ChangePermissionsModel } from "../types/Permissions";
import { MDateInput } from "../components-v2/DateInput";
import { LocalDateFieldModel } from "../common/commonForms";
import { updateDecisionStatus } from "../backoffice/backofficeDomain";
import { PendingDecisionListItem } from "../AdminApp/AdminAppController";
import { MSelect, Select } from "../components-v2/Select";
import { dismissAlert } from "../stores/userPrefsDomain";
import { EmptyState } from "../components-v2/EmptyState";
import { LocalizedTextEditor } from "../text/LocalizedTextEditor";
import {
  InviteUserToCustomRoleDialog,
  RoleDetailsDialog,
} from "../portfolio/contacts/customRoleComponents";
import ExportShareholders from "../shareholderRegister/shareholders/ExportShareholders";
import { PortfolioCompanySelector } from "../shareholderRegister/shareholders/PortfolioCompanySelector";
import { CompanySelectorContextType } from "../shareholderRegister/shareholders/portfolioCompanySelectorDomain";
import { ChangeTransactionDateDialog } from "../shareholderRegister/transactions/transactionLogComponents";

export const VerifyEmailDialog = observer(function VerifyEmailDialog({
  controller,
}: {
  controller: VerifyEmailController;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      onCancel={controller.closeDialog}
      title={t("verifyEmail.title")}
      onSubmit={() => controller.verifyUserEmail()}
      actions={
        isOk(controller.updatedEmailResult) ? (
          <VBox alignItems={"end"}>
            <Button variant={"primary"} label={t("close")} onClick={controller.closeDialog} />
          </VBox>
        ) : (
          <Box>
            <LinkButton
              label={t("verifyEmail.resendCode")}
              onClick={controller.sendVerificationEmail}
            />
            <Box grow justifyContent={"end"} gap={"lg"}>
              <LinkButton label={t("verifyEmail.skip")} onClick={controller.skipVerification} />
              <Button
                variant={"primary"}
                label={t("verifyEmail.verify")}
                onClick={controller.verifyUserEmail}
              />
            </Box>
          </Box>
        )
      }
    >
      <VBox gap={"md"}>
        <Markdown value={t("verifyEmail.codeSentTo", { emailAddress: controller.emailAddress })} />
        <MTextInput
          label={t("verifyEmail.verificationCode")}
          model={controller.verificationCodeModel}
          autoComplete={"off"}
          disabled={isOk(controller.updatedEmailResult)}
        />
        {isOk(controller.updatedEmailResult) && (
          <Alert type={"success"} icon={"check-circle"} message={t("verifyEmail.success")} />
        )}
        {isFailure(controller.updatedEmailResult) && (
          <Alert
            type={"error"}
            message={
              <VBox alignItems={"start"}>
                {t(controller.updatedEmailResult.error)}
                <LinkButton
                  label={t("login.get-a-new-code")}
                  color={"inherit"}
                  onClick={controller.sendVerificationEmail}
                />
              </VBox>
            }
          />
        )}
      </VBox>
    </Dialog>
  );
});

export const EditShareholderDialog = observer(function EditShareholderDialog({
  controller,
  infoLabel,
}: {
  controller: EditSingleShareholderController;
  infoLabel?: TMessage;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (controller.shareholderId) {
      controller.getShareholderDetails(controller.shareholderId);
    } else {
      controller.initEmptyShareholder();
    }
  }, [controller]);

  return (
    <Dialog
      title={
        controller.shareholderId
          ? t("edit-shareholder-dialog.title", { name: controller.shareholderOrCompanyName })
          : t("edit-shareholder-dialog.title.add")
      }
      onCancel={controller.onClose}
      submitLabel={t("save")}
      onSubmit={controller.saveShareholder}
      isSubmitDisabled={
        !controller.model?.isValid ||
        (controller.isBatchUpdate && controller.companies.isNoneSelected)
      }
      isSubmitting={isPending(controller.storeShareholderResult)}
      maxWidth={"sm"}
      height={"full"}
      headerElement={
        controller.showCopyFrom && (
          <Box grow justifyContent={"end"}>
            <DropdownMenu
              {...copyfromAccessibleShareholdersDropdown(
                controller.accessibleShareholderDetails,
                controller.getShareholderDetails,
              )}
            />
          </Box>
        )
      }
      subHeaderElement={
        controller.model ? (
          <VBox borderBottom>
            <Tabs {...editSingleShareholderTabs(controller, controller.model)} />
          </VBox>
        ) : undefined
      }
    >
      {controller.isLoading ? (
        <LoadingIndicator fullscreen />
      ) : controller.model ? (
        <VBox gap={"lg"}>
          {infoLabel && <Alert message={infoLabel} type={"info"} />}
          {controller.showEditSingleShareholderForm && (
            <VBox gap={"lg"}>
              {controller.isBatchUpdate && (
                <Markdown value={t("shareholders.editSingle.multipleCompanies.tip")} />
              )}
              <EditSingleShareholderForm
                model={controller.model}
                fetchShareholders={controller.fetchShareholders}
                isLoadingShareholders={controller.isLoadingShareholders}
                isShareholderView={controller.isShareholderView}
              />
            </VBox>
          )}
          {!controller.showEditSingleShareholderForm && controller.isBatchUpdate && (
            <VBox gap={"lg"} marginTop={"md"}>
              <PortfolioCompanySelector
                title={t("shareholders.editSingle.multipleCompanies.title")}
                companies={controller.companies}
                companySelectorContext={controller.companySelectorContext}
              />
              {controller.companySelectorContext.value?.type !==
                CompanySelectorContextType.COMPANY && (
                <Alert
                  message={
                    <Markdown value={t("shareholders.editSingle.multipleCompanies.information")} />
                  }
                  type={"info"}
                />
              )}
            </VBox>
          )}
        </VBox>
      ) : undefined}
    </Dialog>
  );
});

export const ChangePermissionsDialog = observer(function ChangePermissionsDialog({
  model,
}: {
  model: ChangePermissionsModel;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    model.fetchPermissions();
  }, [model]);
  return (
    <Dialog
      onCancel={model.onClose}
      title={t("permissions.title", { name: model.title })}
      onSubmit={() => model.submitPermissions()}
      submitLabel={t("save")}
      isSubmitDisabled={model.isSubmitDisabled}
      isSubmitting={isPending(model.submitPermissionResult)}
      maxWidth={"sm"}
    >
      {isPending(model.objectPermissionResult) && <LoadingIndicator />}
      {isOk(model.objectPermissionResult) && (
        <VBox gap={"lg"}>
          {isFailure(model.submitPermissionResult) && (
            <Alert type={"error"} message={model.submitPermissionResult.error} />
          )}
          <VBox gap={"md"}>
            <Box className={"bold"}>{t("permissions.currentPermissions")}</Box>
            {model.currentPermissions.map((permission) => {
              const adminInCompany = model.findGranteeFromPermission(permission.grantee);
              return (
                <Box
                  key={permission.id}
                  gap={"lg"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Box>
                    <div className={adminInCompany?.isRevoked ? "strikethrough" : undefined}>
                      {adminInCompany?.name}
                    </div>
                    {permission.isOwner && <Tag color="neutral" label={t("permissions.creator")} />}
                    {adminInCompany?.isRevoked && (
                      <Tag color="error" label={t("permissions.removed-admin")} />
                    )}
                  </Box>
                  <IconButton
                    icon={"trash"}
                    color={"error"}
                    tooltip={
                      permission.isOwner
                        ? t("permissions.delete.disabled")
                        : t("permissions.delete")
                    }
                    disabled={permission.isOwner}
                    onClick={permission.removePermission}
                  />
                </Box>
              );
            })}
          </VBox>
          {model.numberOfAdminsWithoutPermission > 0 && (
            <Box wrap>
              {model.numberOfAdminsWithoutPermission > 1 && (
                <ToggleButton
                  variant={"small"}
                  icon={"plus"}
                  label={t("permissions.addAllAdmins", {
                    n: model.numberOfAdminsWithoutPermission,
                  })}
                  onClick={(e) => {
                    e.preventDefault();
                    model.addAllPermissions();
                  }}
                />
              )}
              {model.adminsWithoutPermission.map((admin) => {
                return (
                  <ToggleButton
                    key={admin.id}
                    variant={"small"}
                    icon={"plus"}
                    label={admin.name}
                    onClick={(e) => {
                      e.preventDefault();
                      model.addPermission(admin);
                    }}
                  />
                );
              })}
            </Box>
          )}
          {model.isSoleAdmin && (
            <Alert
              message={
                <Markdown
                  value={t("permissions.inviteAdmin", {
                    companyName: model.companyName,
                    link: adminUsersSettings(model.orgNo),
                  })}
                />
              }
              type={"info"}
            />
          )}
          {!model.isSoleAdmin && (
            <>
              <Checkbox
                label={t("permissions.notify", { n: model.numberOfNewPermissions })}
                disabled={model.numberOfNewPermissions === 0}
                checked={model.notifyNewPermissions.value}
                onChange={(e) => model.notifyNewPermissions.setValue(e.target.checked)}
              />
              {model.notifyNewPermissions.value && <LocalizedTextEditor model={model.emailBody} />}
            </>
          )}
        </VBox>
      )}
      {isFailure(model.objectPermissionResult) && (
        <Alert type={"warning"} message={model.objectPermissionResult.error} />
      )}
    </Dialog>
  );
});

export const ConfirmDialog = observer(function ConfirmDialog({ state }: { state: ConfirmState }) {
  const { t } = useTranslation();
  return (
    <Dialog
      onCancel={state.onCancel}
      title={state.title ?? { key: "confirm" }}
      onSubmit={state.onConfirm}
      submitLabel={state.confirmLabel ?? { key: "ok" }}
      cancelLabel={state.cancelLabel}
      maxWidth={"sm"}
    >
      <Markdown value={t(state.text)} />
    </Dialog>
  );
});

export const AlertDialog = observer(function AlertDialog({ state }: { state: AlertState }) {
  const { t } = useTranslation();
  return (
    <Dialog
      cancelLabel={t("ok")}
      onCancel={state.onClose}
      title={state.title ?? { key: "info" }}
      maxWidth={"sm"}
    >
      <Markdown value={t(state.text)} />
    </Dialog>
  );
});

export const SelectActorsDialog = observer(function SelectActorsDialog({
  model,
}: {
  model: ActorsSelectorModel;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    model.fetchActors();
  }, [model]);

  const columns: TableColumn<ActorModel>[] = compact([
    model.isMultiSelect && {
      name: "selected",
      label: "",
      align: "left",
      width: "1%",
      notSorted: true,
      getValue: (data) => <Checkbox onChange={noop} checked={data.isSelected} />,
    },
    {
      name: "name",
      label: t("shareholders.name"),
      align: "left",
      getSortValue: (data) => data.actor.name,
      getValue: (data) => <div className={"whitespace-nowrap"}>{data.actor.name}</div>,
    },
    {
      name: "orgNoOrBirthDate",
      label: t("shareholders.birthDateOrOrganisationNumber"),
      align: "left",
      getSortValue: (data) =>
        data.actor.isCompany
          ? data.actor.birthDateOrOrganisationNumber
          : data.actor.birthDateOrOrganisationNumber
            ? toDate(data.actor.birthDateOrOrganisationNumber).getTime()
            : 0,
      getValue: (data) =>
        data.actor.isCompany
          ? formatOrgNum(data.actor.birthDateOrOrganisationNumber)
          : { type: "date", value: data.actor.birthDateOrOrganisationNumber },
    },
    {
      name: "company",
      label: t("company"),
      align: "left",
      notSorted: true,
      getValue: (data) =>
        data.filterAvailableActors(model.companyFilter?.value).length === 1 ? (
          data.filterAvailableActors(model.companyFilter?.value)[0].companyName
        ) : (
          <Select
            value={data.selectedActor}
            onSelect={data.setSelectedActor}
            items={data.filterAvailableActors(model.companyFilter?.value)}
            idFn={(item) => item.reference ?? ""}
            labelFn={(item) => item.companyName ?? ""}
          />
        ),
    },
    model.selectedTab === ActorType.SHAREHOLDER && {
      name: "representativeName",
      label: t("newShareholder.contact_person"),
      align: "left",
      getSortValue: (data) => data.selectedActor.representativeName,
      getValue: (data) =>
        data.selectedActor.representativeName ? data.selectedActor.representativeName : "",
    },
  ]);

  return (
    <Dialog
      onSubmit={model.isMultiSelect ? model.submitActors : undefined}
      submitLabel={t("add")}
      isSubmitting={model.isLoading}
      isSubmitDisabled={model.selectedActors.length === 0}
      onCancel={model.onClose}
      title={model.title}
      maxWidth={"lg"}
      height={"full"}
      subHeaderElement={
        model.tabs.length > 1 ? (
          <Tabs
            scrollable={false}
            tabs={model.tabs.map((tab) => ({
              id: tab,
              label: actorTypeLabel(tab),
              isActive: model.selectedTab === tab,
              onSelect: () => model.setSelectedTab(tab),
            }))}
          />
        ) : undefined
      }
    >
      {model.isLoading ? (
        <LoadingIndicator fullscreen />
      ) : model.actorsByTab.length === 0 ? (
        <VBox grow={"golden"} justifyContent={"center"}>
          <EmptyState illustration={"users-three"} title={t("actors.empty")} />
        </VBox>
      ) : (
        <VBox gap={"lg"} grow>
          <Box gap={"lg"} wrap>
            <Box>
              <MTextInput
                model={model.searchFilter}
                name={"shareholder_filter"}
                placeholder={t("shareholders.filter.search")}
                icon={"magnifying-glass"}
              />
            </Box>
            {model.companyFilter && (
              <Box>
                <MSelect model={model.companyFilter} />
              </Box>
            )}
            {model.companyFilter?.value !== null && model.allCompanyOrgNos.length > 1 && (
              <Box alignItems={"center"}>
                <LinkButton
                  icon={"arrow-counter-clockwise"}
                  label={{ key: "showAllCompanies" }}
                  onClick={() => model.companyFilter?.setValue(null)}
                />
                <Tag label={t("new")} color={"info"} />
              </Box>
            )}
          </Box>
          {model.tabs[0] === ActorType.SHAREHOLDER &&
            model.selectedTab !== ActorType.SHAREHOLDER && (
              <Alert message={{ key: "actors.info.norwegian-shareholders" }} type={"info"} />
            )}
          <DataTable
            hoverable
            tableColumns={columns}
            tableData={model.actorsByTab}
            onRowClick={(row) =>
              model.isMultiSelect ? row.toggleSelectedActor() : model.selectActor(row)
            }
            rowFilter={(s) =>
              s.matches(model.searchFilter.inputValue.toLowerCase(), model.companyFilter?.value)
            }
            getId={(row) => row.id}
            rowActions={(row) =>
              compact([
                row.goToUrl && [
                  "link",
                  {
                    label: { key: "details" },
                    arrow: "out",
                    onClick: [{ type: "load-url", url: row.goToUrl, newWindow: true }],
                  },
                ],
              ])
            }
          />
        </VBox>
      )}
    </Dialog>
  );
});

const DecisionApprovalDialog = observer(function DecisionApprovalDialog({
  model,
  decision,
}: {
  model: LocalDateFieldModel;
  decision: PendingDecisionListItem;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <Dialog
      title={t("followup.decision.approval-date")}
      onSubmit={() =>
        dispatch(
          updateDecisionStatus(decision, "BR_APPROVED", model.validatedValue, {
            type: "closeDialog",
          }),
        )
      }
      onCancel={[{ type: "closeDialog" }]}
      maxWidth={"sm"}
    >
      <MDateInput label={t("followup.decision.approval-date")} model={model} />
    </Dialog>
  );
});

const SubmitForSigningSuccessDialog = observer(function SigningSuccessDialog({
  onClose,
  orgNo,
}: {
  onClose: () => void;
  orgNo?: string;
}) {
  const { t } = useTranslation();
  const userPrefs = useUserPrefs();
  const dispatch = useDispatch();
  const [shouldDismissAlert, setShouldDismissAlert] = useState(true);
  return (
    <Dialog
      title={{ key: "submit-for-signing-success.message" }}
      onSubmit={() => {
        if (shouldDismissAlert) {
          dispatch(dismissAlert(userPrefs, "signingImprovements"));
        }
        onClose();
      }}
      maxWidth={"sm"}
      submitLabel={{ key: "close" }}
    >
      <VBox gap={"lg"}>
        <Markdown
          value={t("submit-for-signing-success.body", {
            companySettingsLink: adminSettings(orgNo ?? ""),
          })}
        />
        <Checkbox
          checked={shouldDismissAlert}
          onChange={(e) => setShouldDismissAlert(e.target.checked)}
          label={t("submit-for-signing-success.dismiss-alert")}
        />
      </VBox>
    </Dialog>
  );
});

export const EnterpriseAccountDialog = observer(function EnterpriseAccountDialog({
  model,
}: {
  model: EnterpriseAccountModel;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={{
        key: model.account ? "enterprise-account.edit-details" : "enterprise-account.add-account",
      }}
      onSubmit={model.save}
      onCancel={[{ type: "closeDialog" }]}
      maxWidth={"xs"}
    >
      <VBox>
        <MTextInput model={model.name} label={{ key: "enterprise-account.account-name" }} />
        <MSelect model={model.status} label={t("enterprise-account.status")} />
        <Box>
          <MDateInput model={model.startTime} label={{ key: "enterprise-account.start-date" }} />
          <MDateInput model={model.endTime} label={{ key: "enterprise-account.end-date" }} />
        </Box>
        <MTextInput
          model={model.shareholderRegisterLimit}
          label={{ key: "enterprise-account.shareholder-register" }}
        />
        <MTextInput
          model={model.digitalCompanyLimit}
          label={{ key: "enterprise-account.digital-company" }}
        />
        <MTextInput model={model.contactName} label={{ key: "enterprise-account.contact-name" }} />
        <MTextInput
          model={model.contactEmail}
          label={{ key: "enterprise-account.contact-email" }}
        />
        <MTextInput
          model={model.primaryCompanyOrgNo}
          label={{ key: "enterprise-account.primary-company" }}
        />
        <MTextInput
          model={model.keyAccountManager}
          label={{ key: "enterprise-account.key-account-manager" }}
        />
        <MTextInput model={model.hubspotLink} label={{ key: "enterprise-account.hubspot" }} />
        <MTextInput model={model.tripletexLink} label={{ key: "enterprise-account.tripletex" }} />
      </VBox>
    </Dialog>
  );
});

export const Dialogs: FC<{ store: RootStoreData }> = observer(({ store }) => {
  const dispatch = useDispatch();
  const onClose = () => dispatch([{ type: "closeDialog" }]);
  const current = last(store.dialogs);
  if (!current) {
    return null;
  }
  switch (current.type) {
    case "acceptCompanyTerms":
      return <AcceptCompanyTermsDialogView model={current.model} />;
    case "payment":
      return <SubscriptionPaymentDialogNew controller={current.model} />;
    case "sendInvitation":
      return <SendInvitationDialog store={current.model} />;
    case "inviteNewAdmin":
      return <InviteNewAdminDialog store={current.model} />;
    case "editEmailAdmin":
      return <EmailEditForm store={current.model} />;
    case "revokeAllAdminAccess":
      return <RevokeAllInAdminForm store={current.model} />;
    case "addShareType":
      return <NewShareClassDialog controller={current.model} />;
    case "activateSubscription":
      return <ActivateSubscriptionDialog controller={current.model} />;
    case "showGeneralMeetingAnnouncement":
      return (
        <LiveGeneralMeetingAnnouncementDialog
          onClose={onClose}
          shareholderPricingInterval={findShareholderPricingInterval(
            current.shareholderCount,
            getShareIntervals(),
          )}
        />
      );
    case "activeShareholderRegisterSubscription":
      return <ActiveShareholderRegisterSubscriptionDialog context={current.context} />;
    case "swedishCompanySearch":
      return <SwedishCompanySearchDialog controller={current.model} />;
    case "norwegianCompanySearch":
      return <NorwegianCompanySearchDialog controller={current.model} />;
    case "newMeeting":
      return (
        <NewMeetingDialogInitializer
          meetingType={current.model.meetingType}
          organisationNumber={current.model.organisationNumber}
          projectName={current.model.projectName}
          onClose={current.model.onCancel}
          projectId={current.model.projectId}
        />
      );
    case "editProjectName":
      return (
        <EditProjectNameDialog
          onSave={current.save}
          onClose={onClose}
          name={current.name}
          isDuplicate={current.isDuplicate ?? false}
        />
      );
    case "preview-file":
      return <FilePreviewDialog {...current} onClose={onClose} />;
    case "showCreatePortfolioDialog":
      return <NewPortfolioDialog {...current} onClose={onClose} />;
    case "verify-email":
      return <VerifyEmailDialog controller={current.controller} />;
    case "edit-shareholder":
      return (
        <EditShareholderDialog controller={current.controller} infoLabel={current.infoLabel} />
      );
    case "change-permissions":
      return <ChangePermissionsDialog model={current.model} />;
    case "confirm":
      return <ConfirmDialog state={current.state} />;
    case "alert":
      return <AlertDialog state={current.state} />;
    case "select-actors":
      return <SelectActorsDialog model={current.model} />;
    case "decision-approval-date":
      return <DecisionApprovalDialog model={current.model} decision={current.decision} />;
    case "submit-for-signing-success":
      return <SubmitForSigningSuccessDialog onClose={onClose} orgNo={current.orgNo} />;
    case "open-role-details":
      return <RoleDetailsDialog model={current.model} />;
    case "invite-custom-role-user":
      return <InviteUserToCustomRoleDialog model={current.model} />;
    case "enterprise-account":
      return <EnterpriseAccountDialog model={current.model} />;
    case "export-shareholders":
      return <ExportShareholders model={current.model} />;
    case "change-transaction-date":
      return (
        <ChangeTransactionDateDialog
          selectedTransaction={current.model.selectedTransaction}
          transactions={current.model.controller.transactionLog}
          onSubmit={current.model.controller.doChangeTransactionDate}
          submitResult={current.model.controller.changeDateResult}
          onCancel={current.model.controller.cancelChangeTransactionDate}
          infoText={current.model.infoText}
        />
      );
  }
});
